import React from 'react';
import { Box, Grid, TextField } from '@mui/material';
import LocationAutocomplete from '../LocationAutocomplete';
import AiRewriteButton from '../../common/AiRewriteButton';
import { addOneHour } from '../../groups/groupFormat';

// The editable form of a group event. Times are datetime-local strings (local wall-clock time).
export interface GroupEventFormValue {
    title: string;
    description: string;
    startDateTime: string;
    endDateTime: string;
    location: string;
}

export const emptyGroupEventForm: GroupEventFormValue = {
    title: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
    location: '',
};

interface GroupEventFieldsProps {
    value: GroupEventFormValue;
    onChange: (value: GroupEventFormValue) => void;
    disabled?: boolean;
    // Hides AI Rewrite, e.g. when the caller's token can't use it yet.
    hideAiRewrite?: boolean;
}

// Title, start/end (the end defaults to an hour after the first start picked), location and
// description + AI Rewrite. Shared by ManageGroup's event dialog and the group setup wizard.
const GroupEventFields: React.FC<GroupEventFieldsProps> = ({ value, onChange, disabled, hideAiRewrite }) => {
    const set = (changes: Partial<GroupEventFormValue>) => onChange({ ...value, ...changes });

    return (
        <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
                <TextField
                    label="Title"
                    value={value.title}
                    onChange={(e) => set({ title: e.target.value })}
                    required
                    fullWidth
                    disabled={disabled}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="Start Date & Time"
                    type="datetime-local"
                    value={value.startDateTime}
                    onChange={(e) => {
                        const startDateTime = e.target.value;
                        // Default the end to an hour later the first time a start is picked.
                        set({ startDateTime, endDateTime: value.endDateTime || addOneHour(startDateTime) });
                    }}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                    disabled={disabled}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    label="End Date & Time"
                    type="datetime-local"
                    value={value.endDateTime}
                    onChange={(e) => set({ endDateTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                    disabled={disabled}
                />
            </Grid>
            <Grid item xs={12}>
                <LocationAutocomplete
                    value={value.location}
                    onChange={(location) => set({ location })}
                    disabled={disabled}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Description"
                    value={value.description}
                    onChange={(e) => set({ description: e.target.value })}
                    multiline
                    rows={4}
                    fullWidth
                    disabled={disabled}
                />
                {!hideAiRewrite && (
                    <Box sx={{ mt: 1 }}>
                        <AiRewriteButton
                            content={value.description}
                            contentType="group-event"
                            disabled={disabled}
                            onAccept={(description) => set({ description })}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default GroupEventFields;
